import { tokens } from '@env0/ui-theme';
import { getAntdTheme as getGeneratedAntdTheme } from '@env0/ui-theme/antd';
import type { ThemeConfig } from 'antd';
import merge from 'lodash/merge';
import type { Breakpoints, Env0Theme } from './themes.constants';
import { getLegacyAntdTheme } from './antd.constants.legacy';

/**
 * Every other antd mapping is generated from Figma's `Bridge - AntD` collection. This one is
 * hand-written, and stays that way: which key each component reads is a function of the antd version,
 * not a design decision, so completing the collection in Figma would generate the wrong thing.
 *
 * New-design only: the legacy branch returns a different config entirely, so switch-off is untouched.
 */
const zIndexTokens = {
  zIndexBase: tokens.zIndex.base,
  zIndexPopupBase: tokens.zIndex.dropdown
};

/**
 * Alert's `ComponentToken` is empty in antd 5.6.4, so these twelve alias tokens are its entire visual
 * restyle and no call site changes to get it.
 *
 * Scoped under `components.Alert`, never on the global token: `colorErrorBg` is also read by
 * `button/style`, `menu/style` and `upload/style/picture`, and it and `colorWarningBg` derive
 * `colorErrorOutline` / `colorWarningOutline` in `theme/util/alias.js`.
 *
 * `colorText` is deliberately absent - `text.<status>` on `surface.<status>.background` fails AA in
 * light for success, warning and error, so status tokens colour the icon and border only.
 *
 * Notification (APO-550) spreads the same set, so the contrast decision is made once; antd 5.6.4's
 * notification style reads only the four foreground keys, ignoring the Bg and Border ones.
 */
export const statusTokens = (mode: Env0Theme['mode']) => {
  const { surface, border, icon } = tokens.theme[mode];

  return {
    colorInfoBg: surface.information.background,
    colorInfoBorder: border.info,
    colorInfo: icon.information,

    colorSuccessBg: surface.success.background,
    colorSuccessBorder: border.success,
    colorSuccess: icon.success,

    colorWarningBg: surface.warning.background,
    colorWarningBorder: border.warning,
    // `icon.warning` is 2.24:1 on this background in light, under the 3:1 non-text floor;
    // `action-hover` is 3.65:1. Dark clears it at 8.76:1. Token gap raised with design.
    colorWarning: mode === 'light' ? surface.warning['action-hover'] : icon.warning,

    colorErrorBg: surface.error.background,
    colorErrorBorder: border.error,
    colorError: icon.error
  };
};

/**
 * Modal has no `zIndexPopup` in its ComponentToken and reads the `zIndexPopupBase` seed directly. The
 * rest read `zIndexPopup`, the value their style consumes; seeding those instead renders the DS number
 * plus antd's per-component offset (popover +30, notification +50, tooltip +70), so no DS value would
 * reach the DOM.
 *
 * The ladder ranks dropdown below drawer and modal, which holds only because AntdWrapper portals a
 * nested popup into its container: 5.6.4 has no `zIndexContext`, so otherwise every popup lands on
 * document.body and a Select inside a Popover renders behind it.
 */
const componentOverrides = ({ mode }: Env0Theme) => ({
  Modal: { zIndexPopupBase: tokens.zIndex.modal },
  Drawer: {
    zIndexPopup: tokens.zIndex.drawer,
    // Equal to antd 5.6.4's default today; pinned so the footer follows the DS ladder, not antd's.
    footerPaddingBlock: toPx(tokens.spacing.padding.xs),
    footerPaddingInline: toPx(tokens.spacing.padding.lg)
  },
  Popover: { zIndexPopup: tokens.zIndex.popover },
  Notification: {
    zIndexPopup: tokens.zIndex.toast,
    // Without this antd's own dark surface is 1.11:1 against the page and its shadow is black on
    // black, leaving the toast with no visible edge. Same value the bridge gives Drawer.
    colorBgElevated: tokens.theme[mode].surface.main['container-on-card'],
    ...statusIconTokens(mode)
  },
  /**
   * The generated bridge maps surface/main/cards onto dark's foreground here, giving 1.33:1 on the
   * spotlight. Deleting the block does not fix it either - the light global is 1.43:1 on the same
   * background. One value for both modes because the spotlight itself is one colour in both; a
   * primitive because no semantic token means "foreground over the spotlight". Raised with design so
   * the next `Bridge - AntD` rebuild does not reintroduce it.
   */
  Tooltip: {
    zIndexPopup: tokens.zIndex.tooltip,
    colorTextLightSolid: tokens.colors.neutral.white
  }
});

const toPx = (value: string) => parseInt(value, 10);

/**
 * Neither Result nor Skeleton is in the generated bridge and Figma specs neither — extrapolated from
 * the ramp per D1. Takes the mode because the Skeleton pair is colour: fixed light greys would give a
 * near-white shimmer on dark surfaces, so dark sweeps from the dark end of the same scale (700 → 650).
 */
const feedbackComponents = (mode: 'light' | 'dark') => ({
  Result: {
    // h3, not h4: a Result is a full-page outcome.
    titleFontSize: toPx(tokens.typography.fontSize.heading.h3),
    subtitleFontSize: toPx(tokens.typography.fontSize.body.md),
    iconFontSize: toPx(tokens.spacing.scale['1000'])
  },
  Skeleton: {
    gradientFromColor: mode === 'dark' ? tokens.colors.neutral['700'] : tokens.colors.neutral['300'],
    gradientToColor: mode === 'dark' ? tokens.colors.neutral['650'] : tokens.colors.neutral['200'],
    blockRadius: toPx(tokens.spacing.radius['100'])
  }
});

/**
/** The icon half of `statusTokens`: a toast has no tinted fill or border to colour. */
const statusIconTokens = (mode: Env0Theme['mode']) => {
  const { colorSuccess, colorInfo, colorWarning, colorError } = statusTokens(mode);

  return { colorSuccess, colorInfo, colorWarning, colorError };
};

/**
 * Hand-written for the same reason as the z-index mappings: which alias token antd reads is a fact
 * about the version, not a design decision. Button's ComponentToken is empty in 5.6.4, so only the
 * alias tokens scoped here take effect - each was measured reaching the emitted CSS. Real values
 * rather than `var()`: antd derives palettes from what it is given.
 *
 * Mechanics worth knowing before editing:
 * - Radius and label size are read per size variant, so setting only the base keys leaves large and
 *   small on antd's own ladder.
 * - Horizontal padding is `buttonPaddingHorizontal - lineWidth`; the border adds the pixel back, so
 *   the token value is the visual inset. Small's is hardcoded to 8 in antd and is not configurable.
 * - Secondary is antd's `default` type, and Figma binds it no surface variable at all - a border and
 *   a label over nothing, hence `transparent`.
 */
const buttonComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border } = tokens.theme[mode];
  const px = (value: string) => parseInt(value, 10);

  return {
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
    borderRadius: px(tokens.spacing.radius['100']),
    borderRadiusLG: px(tokens.spacing.radius['100']),
    borderRadiusSM: px(tokens.spacing.radius['100']),
    lineWidth: px(tokens.spacing.borderWidth.xs),
    fontSize: 14,
    fontSizeLG: 14,
    paddingContentHorizontal: px(tokens.spacing.padding.lg),

    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    colorPrimaryActive: surface.primary['action-hover'],
    colorTextLightSolid: text.onAction,

    colorBgContainer: 'transparent',
    colorBorder: border.secondary,
    // `text.heading`, not the `text.onAction` Figma binds: that token means "over a lime fill" and is
    // black in both modes, so on an unfilled Secondary it vanished in dark. Same value in light.
    colorText: text.heading,

    colorBgContainerDisabled: surface.disabled.action,
    // Dark cannot follow Figma: text.onDisabled and surface.disabled.action are both #4d4d4d there,
    // so the label would be invisible against its own fill. Figma has no dark frames (D3).
    colorTextDisabled: mode === 'dark' ? text.disabled : text.onDisabled
  };
};

type Mode = Env0Theme['mode'];

/**
 * Hand-written for the same reason as the z-index mappings: which token antd reads is a fact about 5.6.4,
 * not a design decision. Every key here was measured reaching the emitted CSS; absent keys were measured
 * not reaching it.
 *
 * Switch sets no geometry on purpose. 5.6.4 has no Switch ComponentToken and derives track, handle and
 * min-width from `fontSize`, `lineHeight` and `controlHeight` — values that already equal the Figma
 * variables. Those variables are named after a later antd's ComponentToken keys, which do not exist here.
 */
const switchComponent = (mode: Mode) => {
  const { surface } = tokens.theme[mode];

  return {
    colorPrimary: surface.switch['track-active'],
    colorPrimaryHover: surface.switch['track-active'],
    colorTextQuaternary: surface.switch['track-inactive'],
    colorTextTertiary: surface.switch['track-inactive'],
    // The handle, despite antd deriving `switchBg` from `colorBgContainer` — that key does not reach at
    // component scope, and without this the dark handle stays white.
    colorWhite: surface.switch.handle
  };
};

/**
 * Deliberately minimal, and defensive rather than load-bearing: `BridgedCard` routes every raw-antd call
 * site onto the common wrapper on the new-design path, so this block only has to keep a stray antd Card
 * from looking wrong. The card's real restyle lives in `components/common/card.tsx`.
 *
 * Card's own `ComponentToken` in 5.6.4 is header/actions geometry only, so everything below except
 * `headerBg` / `headerFontSize` is an alias token scoped to the component. Which ones reach was probed,
 * not assumed: `colorBorderSecondary` paints every Card border and `colorBorder` never reaches at all,
 * `paddingLG` drives both head and body, and `borderRadiusLG` drives the card plus its head/body/cover
 * corners. `fontSizeLG` reaches only `.ant-card-meta-title`, so it is not set here.
 *
 * `headerBg: transparent` because the wrapper draws the header rule itself - antd's filled head would
 * double it.
 */
const cardComponent = (mode: Mode) => {
  const { surface, text, border } = tokens.theme[mode];

  return {
    // Measured off all 19 billing-card frames on the Page template page: radius 12, 1px inside stroke.
    borderRadiusLG: toPx(tokens.spacing.radius['400']),
    lineWidth: toPx(tokens.spacing.borderWidth.xs),
    paddingLG: toPx(tokens.spacing.padding.xxl),
    colorBgContainer: surface.main.cards,
    colorBorderSecondary: border.primary,
    colorTextHeading: text.heading,
    headerBg: 'transparent',
    headerFontSize: toPx(tokens.typography.fontSize.heading.h4)
  };
};

const checkboxComponent = (mode: Mode) => {
  const { surface, text, icon } = tokens.theme[mode];

  return {
    // Not `checkboxSize`: `getStyle` re-derives that from this key through `mergeToken`, discarding it.
    controlInteractiveSize: 16,
    borderRadiusSM: parseInt(tokens.spacing.radius['100'], 10),
    lineWidth: parseInt(tokens.spacing.borderWidth.xs, 10),

    // antd paints the checked border from `colorPrimary` too, so Figma's separate `border.action` cannot
    // be honoured — identical in light, `#d2f740` rather than `#ddf970` in dark.
    colorPrimary: surface.primary.action,
    colorPrimaryHover: surface.primary['action-hover'],
    // The checkmark. antd draws it with `colorWhite`, which would be white on lime.
    colorWhite: text.onAction,
    colorBorder: icon.secondary,
    colorTextDisabled: text.disabled,
    colorBgContainerDisabled: icon.disabled
  };
};

/**
 * Radio has no Figma component, so per D1 it extrapolates geometry from checkbox. Colours stay as the
 * generated `Bridge - AntD` collection sets them — a darker green than checkbox's lime, raised with design
 * rather than corrected here — so `colorPrimary` is deliberately absent: setting it would override them.
 */
const radioComponent = (mode: Mode) => {
  const { icon } = tokens.theme[mode];

  return {
    radioSize: 16,
    wrapperMarginInlineEnd: parseInt(tokens.spacing.spacing.xxs, 10),
    dotColorDisabled: icon.disabled
  };
};

/**
 * The `--component-field-*` group is the design's own form-field token set. Resolved from
 * `tokens.theme[mode]` rather than `tokens.components.field`, which is light-only; antd derives
 * palettes at theme-build time and needs real per-mode values.
 *
 * Mechanics measured by rendering with sentinel values and reading the emitted sheet:
 * - `.ant-input` declares no height. Height is padding + line-height + border, and antd derives the
 *   vertical padding from `controlHeight` after this merge, clamped at 3px - so today's 28px renders
 *   30. 32 is exact, and `controlHeightSM` 28 is exact too (its clamp floor is 0).
 * - Horizontal inset is `paddingSM - lineWidth` plus the border, i.e. `paddingSM` is the visual inset.
 * - Hover and focus border are one declaration in 5.6.4; both tokens alias --border-focus-primary.
 * - Disabled reuses `colorBorder`, so --component-field-border-disabled cannot come from here.
 * - The addon strip reads `colorFillAlter`; 5.6.4 has no `addonBg`.
 * - The generated bridge sets Input.controlHeight 24, which renders a 30px field. Overridden here.
 */
const fieldComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border, icon } = tokens.theme[mode];
  const px = (value: string) => parseInt(value, 10);
  const radius = px(tokens.spacing.radius['100']);

  return {
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
    borderRadius: radius,
    borderRadiusLG: radius,
    borderRadiusSM: radius,
    lineWidth: px(tokens.spacing.borderWidth.xs),
    fontSize: 14,
    paddingSM: px(tokens.spacing.padding.md),

    colorBgContainer: surface.main.cards,
    colorFillAlter: surface.main.cards,
    colorBorder: border.inputFieldDefault2,
    colorPrimaryHover: border.focusPrimary,
    colorText: text.heading,
    colorTextPlaceholder: text.secondary,
    colorTextDescription: icon.secondary,

    controlOutline: border.focusPrimary,
    controlOutlineWidth: px(tokens.spacing.borderWidth.sm),

    // Disabled keeps the resting fill: both background tokens alias surface/main/cards in both modes,
    // so border and text carry the state. The app-wide colorBgContainerDisabled would grey it.
    colorBgContainerDisabled: surface.main.cards,
    colorTextDisabled: text.disabled,
    colorError: border.error
  };
};

/**
 * One token, and only because dark needs it.
 *
 * antd 5.6.4 defaults `horizontalItemSelectedColor` to `colorPrimary` and both horizontal backgrounds
 * to `transparent` (`antd/lib/menu/style/index.js`), so in light the generated bridge's lime already
 * produces Figma's underline-on-nothing and a block here would restate antd's own derivation. In dark
 * it does not: antd's `darkAlgorithm` shifts `colorPrimary` to `#b6d539`, and the DS holds
 * `surface.primary.action` at the same lime in both modes. Setting it pins the token value.
 *
 * Measured in the browser, not reasoned about — the light-only spec passed with this deleted, which is
 * how the first version of this shipped, and it took a dark browser pass to catch.
 *
 * What antd cannot do is give the label a different colour from the underline: `theme.js` draws both
 * from this one token. `components/common/menu.tsx` repaints the label on `.ant-menu-title-content`,
 * which antd gives only a transition, so it needs no `!important` and no doubled class.
 *
 * `Menu.itemSelectedBg` in the generated bridge is the lime — a nav-rail value. It reaches vertical
 * menus only, so it never touches the tab bar; `drift-analysis-menu.tsx` already neutralises it
 * locally, and the rail itself belongs to C2/C3. Raise it there rather than repainting them from here.
 */
const menuComponent = (mode: 'light' | 'dark') => ({
  horizontalItemSelectedColor: tokens.theme[mode].surface.primary.action
});

/**
 * Tabs has exactly one consumer (`components/common/tabs.tsx`), so unlike Menu there is no other
 * family to repaint. Only the keys that actually differ from antd 5.6.4's defaults are set:
 * `inkBarColor` already derives from `colorPrimary` and would be the same lime either way, so setting
 * it would be untestable noise for the same reason Menu has no block.
 *
 * The label tokens do differ — antd defaults them to `colorPrimary` too, which would put a lime label
 * on a lime ink bar. `cardBg` in the generated bridge is `#151514` in both modes, a dark value on a
 * light card; corrected here per mode.
 */
const tabsComponent = (mode: 'light' | 'dark') => {
  const { surface, text } = tokens.theme[mode];

  return {
    itemHoverColor: text.heading,
    itemSelectedColor: text.heading,
    itemActiveColor: text.heading,
    cardBg: surface.main.cards
  };
};

/**
 * Select declares a ComponentToken of only `{ zIndexPopup }` in 5.6.4, so every key below is an *alias*
 * token scoped to the component name, and a key outside the set `select/style` actually reads is silently
 * inert — `optionSelectedBg` is one such: it does not exist in 5.6.4, so `controlItemBgActive` carries the
 * selected-option background instead.
 *
 * The three values the generated bridge gets wrong are corrected here: `controlHeight` and `fontSize`
 * describe the pre-restyle select, and `colorPrimaryHover` is a dark olive rather than the focus border.
 */
const selectComponent = (mode: 'light' | 'dark') => {
  const { surface, text, border, icon } = tokens.theme[mode];
  const { radius } = tokens.spacing;

  return {
    controlHeight: 32,
    // The generated bridge set this equal to controlHeight, which made `size="small"` a no-op.
    controlHeightSM: 28,
    borderRadius: toPx(radius[200]),
    // The option row and the multi-select tag; antd defaults both to 4.
    borderRadiusSM: toPx(radius[200]),
    // The panel, shared with the TreeSelect popup.
    borderRadiusLG: toPx(radius[200]),
    fontSize: 14,

    // Trigger.
    colorBgContainer: surface.main.cards,
    colorBgContainerDisabled: surface.main.cards,
    colorBorder: border.inputFieldDefault2,
    colorPrimaryHover: border.focusPrimary,
    controlOutline: surface.primary.background,
    colorText: text.heading,
    colorTextPlaceholder: text.secondary,
    colorTextDisabled: text.disabled,
    // The suffix icon and the allowClear cross, not body text.
    colorTextQuaternary: icon.secondary,
    colorTextTertiary: icon.primary,
    // The multi-select tag background, and its remove cross resting and hovered.
    colorFillSecondary: surface.main['element-on-card'],
    colorIcon: icon.secondary,
    colorIconHover: icon.primary,

    // Panel.
    colorBgElevated: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    colorTextDescription: text.secondary,
    // The checkmark on the selected option.
    colorPrimary: surface.primary.action
  };
};

/**
 * `genTreeStyle` derives the row height from `controlHeightSM` and the row padding from `paddingXS / 2` —
 * under Select the same `controlHeightSM` is the small trigger height instead, so the two sections read one
 * name as two different measurements.
 *
 * Set under both names on purpose: `genComponentStyleHook` scopes overrides by the literal name the hook
 * registered with, and `tree-select/style` registers as `TreeSelect` while generating its rows with Tree's
 * `genTreeStyle`, so a TreeSelect popup never sees `components.Tree`. Both hooks also run `checkbox/style`
 * under their own scope, which is why `colorPrimary` here is what paints tree checkboxes.
 */
const treeRowTokens = (mode: 'light' | 'dark') => {
  const { surface, text, border } = tokens.theme[mode];

  return {
    controlHeightSM: 32,
    paddingXS: toPx(tokens.spacing.padding.xs),
    borderRadius: toPx(tokens.spacing.radius[200]),
    colorBgContainer: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    colorPrimary: surface.primary.action,
    colorTextDisabled: text.disabled,
    // The connector lines under `showLine`.
    colorBorder: border.primary
  };
};

// Dropdown ships its own menu styles and reads none of `components.Menu`, so this is the only token path
// to a dropdown item.

const dropdownComponent = (mode: 'light' | 'dark') => {
  const { surface, text } = tokens.theme[mode];

  return {
    colorBgElevated: surface.main.cards,
    controlItemBgHover: surface.main['element-on-card'],
    controlItemBgActive: surface.secondary.background,
    controlItemBgActiveHover: surface.secondary['action-hover'],
    colorText: text.heading,
    colorTextDescription: text.disabled,
    colorSplit: surface.main['element-on-card'],
    // The item block reads `fontSize`, and its vertical padding derives from it. `fontSizeSM` reaches only
    // the item icon, so overriding that one would leave the generated section's `fontSize: 12` on the rows.
    fontSize: 14,
    borderRadiusLG: toPx(tokens.spacing.radius[200]),
    borderRadiusSM: toPx(tokens.spacing.radius[200])
  };
};

/**
 * antd wants numbers; the theme carries px strings so emotion can interpolate them directly.
 *
 * All 17 keys are set, not just the 6 bases: antd 5.6.4 does not re-derive the *Min/*Max aliases when
 * a base is overridden, so moving screenMD to 900 while screenSMMax stays 767 leaves 768-899 uncovered.
 */
const toScreenTokens = ({ breakpoints }: Env0Theme) => {
  const px = (step: keyof Breakpoints) => parseInt(breakpoints[step], 10);
  const xs = px('xs');
  const sm = px('sm');
  const md = px('md');
  const lg = px('lg');
  const xl = px('xl');
  const xxl = px('xxl');

  return {
    screenXS: xs,
    screenXSMin: xs,
    screenXSMax: sm - 1,
    screenSM: sm,
    screenSMMin: sm,
    screenSMMax: md - 1,
    screenMD: md,
    screenMDMin: md,
    screenMDMax: lg - 1,
    screenLG: lg,
    screenLGMin: lg,
    screenLGMax: xl - 1,
    screenXL: xl,
    screenXLMin: xl,
    screenXLMax: xxl - 1,
    screenXXL: xxl,
    screenXXLMin: xxl
  };
};

export const getAntdTheme = (theme: Env0Theme): ThemeConfig => {
  if (!theme.isNewDesign) {
    return getLegacyAntdTheme(theme);
  }

  // getGeneratedAntdTheme returns a module-level object, so every addition has to be a copy.
  const generated = getGeneratedAntdTheme(theme.mode);

  return {
    ...generated,
    token: { ...generated.token, ...zIndexTokens, ...toScreenTokens(theme) },
    // InputNumber takes the same block: its own ComponentToken in 5.6.4 is controlWidth, handleWidth,
    // handleFontSize and handleVisible, none of which the design specifies.
    components: merge({}, generated.components, componentOverrides(theme), feedbackComponents(theme.mode), {
      Alert: statusTokens(theme.mode),
      Button: buttonComponent(theme.mode),
      Input: fieldComponent(theme.mode),
      InputNumber: fieldComponent(theme.mode),
      Menu: menuComponent(theme.mode),
      Tabs: tabsComponent(theme.mode),
      Switch: switchComponent(theme.mode),
      Card: cardComponent(theme.mode),
      Checkbox: checkboxComponent(theme.mode),
      Radio: radioComponent(theme.mode),
      Select: selectComponent(theme.mode),
      Tree: treeRowTokens(theme.mode),
      TreeSelect: treeRowTokens(theme.mode),
      Dropdown: dropdownComponent(theme.mode)
    })
  };
};
